import type { TFunction } from "i18next";
import type { PlosTranslations } from "plos/src/contexts/PlosTranslationsContext";
import { Trans } from "react-i18next";

export const getPlosTranslations = (t: TFunction): PlosTranslations => ({
  addPlayer: (playerName?: string) =>
    playerName
      ? t("plos:addPlayerNamed", "Add player {{playerName}}", { playerName })
      : t("plos:addPlayer", "Add player"),
  back: t("plos:back", "Back"),
  captain: t("plos:captain", "Captain"),
  carouselPanel: {
    scrollLeft: t("plos:carouselPanel.scrollLeft", "Scroll left"),
    scrollRight: t("plos:carouselPanel.scrollRight", "Scroll right"),
  },
  closeButton: t("plos:closeButton", "Close"),
  deadlineBar: {
    deadline: (deadlineTime, timeProps) => (
      <Trans
        ns="plos"
        i18nKey="deadlineBar.deadline"
        components={{ time: <time {...timeProps} /> }}
        values={{ deadlineTime }}
      />
    ),
  },
  dialog: {
    closeButton: t("plos:dialog.closeButton", "Close dialog"),
  },
  dreamTeam: t("plos:dreamTeam", "Dream Team"),
  elementDialogButton: {
    viewPlayerInformation: t(
      "plos:elementDialogButton.viewPlayerInformation",
      "View player information",
    ),
  },
  errorFallback: {
    title: t("plos:errorFallback.title", "Error"),
    errorMessage: t(
      "plos:errorFallback.errorMessage",
      "We're sorry - something's gone wrong.",
    ),
    reportMessage: t(
      "plos:errorFallback.reportMessage",
      "Our team has been notified, but click here to fill out a report.",
    ),
    reportButton: t("plos:errorFallback.reportButton", "Report feedback"),
  },
  externalSite: t("plos:externalSite", "external site"),
  fieldError: {
    valueMissing: t(
      "plos:forms.fieldError.valueMissing",
      "This field is required.",
    ),
    typeMismatch: t("plos:forms.fieldError.typeMismatch", "Invalid format."),
  },
  filterChip: {
    reset: t("plos:filterChip.reset", "Reset filters"),
    noneSelected: (label: string) =>
      t("plos:filterChip.noneSelected", "{{label}}, none selected", { label }),
    selected: (label: string, chipText: string) =>
      t("plos:filterChip.selected", "{{label}}, {{chipText}} selected", {
        label,
        chipText,
      }),
  },
  filterOptions: t("plos:filterOptions", "Filter options"),
  filterSheet: {
    saveFilterChanges: t(
      "plos:filterSheet.saveFilterChanges",
      "Save filter changes",
    ),
    saveChanges: t("plos:filterSheet.saveChanges", "Save changes"),
  },
  fixtures: {
    all: t("plos:fixtures.all", "All"),
    addFixturesToCalendar: t(
      "plos:fixtures.addFixturesToCalendar",
      "Add fixtures to calendar",
    ),
    broadcasters: t("plos:fixtures.broadcasters", "Broadcasters"),
    deadline: (deadlineTime, timeProps) => (
      <Trans
        ns="plos"
        i18nKey="fixtures.deadline"
        components={{ time: <time {...timeProps} /> }}
        values={{ deadlineTime }}
      />
    ),
    deadlineNote: t(
      "plos:fixtures.deadlineNote",
      "*All times are shown in your local time",
    ),
    fixture: t("plos:fixtures.fixture", "Fixture"),
    fixtureStats: (homeTeam, awayTeam) =>
      t(
        "plos:fixtures.fixtureStats",
        "Show match stats for {{homeTeam}} versus {{awayTeam}}",
        {
          homeTeam,
          awayTeam,
        },
      ),
    fixtureTimeAbbreviation: t("plos:fixtures.fixtureTimeAbbreviation", "FT"),
    fixtures: t("plos:fixtures.fixtures", "Fixtures"),
    multipleBroadcasters: t(
      "plos:fixtures.multipleBroadcasters",
      "Multiple Broadcasters",
    ),
    noFixture: t("plos:fixtures.noFixture", "No fixture"),
    viewBroadcasters: t("plos:fixtures.viewBroadcasters", "View broadcasters"),
    watchLiveOn: t("plos:fixtures.watchLiveOn", "Watch Live On"),
  },
  formSelect: {
    placeholder: t("plos:forms.formSelect.placeholder", "Select an option"),
  },
  gameTabs: {
    navLabel: t("plos:gameTabs.navLabel", "Fantasy games"),
    classic: t("plos:gameTabs.classic", "Fantasy"),
    challenge: t("plos:gameTabs.challenge", "FPL Challenge"),
    draft: t("plos:gameTabs.draft", "FPL Draft"),
  },
  matchDetails: t("plos:matchDetails", "Match details"),
  moreInfo: t("plos:moreInfo", "More info"),
  nextGameweek: t("plos:nextGameweek", "Next gameweek"),
  noDataAvailable: t("plos:noDataAvailable", "No data available"),
  noStatsAvailable: t("plos:noStatsAvailable", "No stats available"),
  notApplicableAbbreviation: t("plos:notApplicableAbbreviation", "N/A"),
  opensInNewTab: t("plos:opensInNewTab", "Opens in new tab"),
  paginator: {
    first: t("plos:paginator.first", "First"),
    last: t("plos:paginator.last", "Last"),
    next: t("plos:paginator.next", "Next"),
    pageOfTotal: (page, totalPages) => (
      <Trans
        ns="plos"
        i18nKey="paginator.pageOfTotal"
        components={{ strong: <strong /> }}
        values={{ page, totalPages }}
      />
    ),
    previous: t("plos:paginator.previous", "Previous"),
  },
  player: t("plos:player", "Player"),
  playerNotes: {
    label: t("plos:playerNotes.label", "Player notes"),
    tooltipText: t(
      "plos:playerNotes.tooltipText",
      "View status update on player profile",
    ),
  },
  playerStats: t("plos:playerStats", "Player stats"),
  points: t("plos:points", "points"),
  pointsAbbreviation: t("plos:pointsAbbreviation", "Pts"),
  popoverSelectLabel: t("plos:popoverSelectLabel", "Select an option"),
  previousGameweek: t("plos:previousGameweek", "Previous gameweek"),
  removePlayer: (playerName?: string) =>
    playerName
      ? t("plos:removePlayerNamed", "Remove player {{playerName}}", {
          playerName,
        })
      : t("plos:removePlayer", "Remove player"),
  rowLoading: t("plos:rowLoading", "row loading"),
  scoreboard: {
    bank: t("plos:scoreboard.bank", "Bank"),
    budget: t("plos:scoreboard.budget", "Budget"),
    cost: t("plos:scoreboard.cost", "Cost"),
    freeHit: t("plos:scoreboard.freeHit", "free hit"),
    freeTransfers: t("plos:scoreboard.freeTransfers", "Free transfers"),
    playersSelected: t("plos:scoreboard.playersSelected", "Players selected"),
    transferCostsValue: (costs: number) =>
      t("plos:scoreboard.transferCostsValue", "{{costs}} pts", { costs }),
    unlimited: t("plos:scoreboard.unlimited", "Unlimited"),
    wildcard: t("plos:scoreboard.wildcard", "wildcard"),
  },
  searchField: {
    placeholder: t("plos:forms.searchField.placeholder", "Search"),
    cancelButton: t("plos:forms.searchField.cancelButton", "Cancel"),
  },
  statsAbbreviation: t("plos:statsAbbreviation", "Stats"),
  status: t("plos:status", "Status"),
  switchPlayer: t("plos:switchPlayer", "Switch player"),
  teamName: t("plos:teamName", "Team Name"),
  toBeConfirmedAbbreviation: t("plos:toBeConfirmedAbbreviation", "TBC"),
  viceCaptain: t("plos:viceCaptain", "Vice Captain"),
});
